'use client';

import React, { useState } from 'react';
import QuestionStep from './steps/question-step/QuestionStep';
import FormStep from './steps/form-step/FormStep';
import { ProgressiveFormProps } from '@/sections/forms/progressive-form/types';
import { Headline } from '@/components/headline/Headline';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from '@/lib/locale-context';
import { BaseImage } from '@/components/baseImage/BaseImage';
import IntroStep from './steps/intro-step/IntroStep';
import './styles.css';

const ProgressiveForms: React.FC<ProgressiveFormProps> = ({
  title,
  formHeadline,
  successMessage,
  description,
  questions = [],
  hiddenFields = [],
  introHeadline,
  introDescription,
  introDisclaimer,
  introImage,
  introButton,
  sectionId,
}) => {
  const { t } = useTranslations();
  const hasCover = Boolean(introHeadline?.title);
  const [showCover, setShowCover] = useState(hasCover);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, questions.length));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const resetToFirstStep = () => {
    setCurrentStep(0);
    setAnswers({});
  };

  const handleChange = (value: string | string[]) => {
    if (Array.isArray(value)) {
      // checkbox
      setAnswers((prev) => ({
        ...prev,
        [currentStep]: value, // string[]
      }));
    } else {
      // radio/input/select
      setAnswers((prev) => ({
        ...prev,
        [currentStep]: value, // string
      }));
    }
  };

  const formatQuestionsWithAnswers = () => {
    return questions
      .map((question, index) => {
        const questionText =
          typeof question.headline === 'string'
            ? question.headline
            : question.headline ||
              t('progressiveForm.question', {
                current: index + 1,
                total: questions.length,
              });

        const answer = answers[index] || t('progressiveForm.noAnswer');

        return `${index + 1}. ${questionText}:\n- ${answer}`;
      })
      .join('\n\n');
  };

  const formattedCallbackNotes = formatQuestionsWithAnswers();
  const totalSteps = questions.length + 1;
  const step = questions[currentStep];

  if (showCover) {
    return (
      <div
        id={sectionId}
        className="relative flex min-h-[770px] items-center justify-center overflow-hidden bg-brand-700"
      >
        <BaseImage
          image={{
            url: '/ellipse-3.png',
          }}
          width="1920"
          height="600"
          alt="Background Glow"
          className="pointer-events-none absolute bottom-[-10%] left-1/2 z-0 w-full max-w-none -translate-x-1/2"
          classNameImage="w-full"
        />
        <div className="z-10 w-full">
          <IntroStep
            introHeadline={introHeadline}
            introDescription={introDescription}
            introDisclaimer={introDisclaimer}
            introImage={introImage}
            introButton={introButton}
            onStart={() => setShowCover(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      id={sectionId}
      className="relative flex min-h-[770px] items-center justify-center overflow-hidden bg-brand-700"
    >
      <BaseImage
        image={{
          url: '/ellipse-3.png',
        }}
        width="1920"
        height="600"
        alt="Background Glow"
        className="pointer-events-none absolute bottom-[-10%] left-1/2 z-0 w-full max-w-none -translate-x-1/2"
        classNameImage="w-full"
      />

      <div className="container z-10 mx-auto space-y-12 px-4 py-6">
        <div className="text-center text-white">
          {title && <Headline {...title} />}
          {description && (
            <SerializeDescription
              className="[&_*]:!text-white"
              description={description}
            />
          )}
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {currentStep >= questions.length ? (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <FormStep
                onBack={handleBack}
                onNext={handleNext}
                totalSteps={totalSteps}
                onResetToFirstStep={resetToFirstStep}
                step={currentStep + 1}
                callbackRequestNotes={formattedCallbackNotes}
                hiddenFields={hiddenFields}
                formHeadline={formHeadline}
                successMessage={successMessage}
              />
            </motion.div>
          ) : (
            <motion.div
              key={currentStep}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <QuestionStep
                {...step}
                selectedValue={answers[currentStep] ?? ''}
                onChange={handleChange}
                onNext={handleNext}
                onBack={handleBack}
                step={currentStep + 1}
                totalSteps={totalSteps}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProgressiveForms;
